import { stripIndent } from 'common-tags';
import { Client } from '../client/Client';
import { ClientId } from '../client/ClientId';
import { FullAuditable, FullAuditableKeys, HasId } from '../common/model/traits';
import { Upload, UploadId } from '../upload/Upload';
import { Omit } from '../common/lang/types';
import { ApiRoute, HttpMethod } from '../common/api/routes';
import { RelativeUrl } from '../common/lang/web';

/**
 * @see {@link https://www.freetype.org/freetype2/docs/glyphs/glyphs-3.html|FreeType.org} for an overview of these properties
 */
export interface BuilderFontCustomMetrics {
    unitsPerEm: number;
    ascent: number;
    descent: number;
    lineGap: number;
    underlinePosition: number;
    underlineThickness: number;
    italicAngle: number;
    capHeight?: number;
    xHeight?: number;
    /**
     * @see {@link https://www.freetype.org/freetype2/docs/glyphs/glyphs-3.html#section-5|FreeType.org bounding box}
     */
    bbox: {
        minX: number;
        minY: number;
        maxX: number;
        maxY: number;
    };
}

export interface BuilderFontCustom extends HasId, FullAuditable {
    clientId: ClientId;
    family: string;
    metrics: BuilderFontCustomMetrics;
    stretch: string;
    style: string;
    weight: string;
    uploadId: UploadId;

    client?: Client;
    upload?: Upload;
}

export type BuilderFontCustomDto = Omit<BuilderFontCustom, FullAuditableKeys | 'client' | 'upload'> & {
    upload: Pick<Upload, 'ext' | 'filename' | 'id' | 'url'>;
};

export interface BuilderFontConfig {
    custom: BuilderFontCustomDto[];
}

export function getBuilderFontCustomCssUrl(builderFontCustomId: number): RelativeUrl {
    return RelativeUrl.from(`/fonts/custom/${builderFontCustomId}.css`);
}

const MIME_TYPE_MAP: { [key: string]: string } = {
    eot: 'application/vnd.ms-fontobject',
    otf: 'font/otf', // Should this be "application/x-font-opentype"?
    ttf: 'font/ttf', // Should this be "application/x-font-truetype"?
    woff: 'application/x-font-woff',
    woff2: 'application/x-font-woff2',
};

export function getFontMimeType(extension: string): string | undefined {
    return MIME_TYPE_MAP[extension] || undefined;
}

const FONT_FORMATS: {
    [key: string]: string;
} = {
    eot: 'eot',
    otf: 'opentype',
    ttf: 'truetype',
    woff: 'woff',
    woff2: 'woff2',
};
function getFontFormat(ext: string) {
    // eslint-disable-next-line no-param-reassign
    ext = ext.toLowerCase();
    const fontFormat = FONT_FORMATS[ext];
    if (!fontFormat) {
        return '';
    } else {
        return ` format("${fontFormat}")`;
    }
}

function getFontLocalUrl(builderFontCustomId: number) {
    const url = `/fonts/custom/${builderFontCustomId}`;
    return url;
}

type GenerateFontCssDto = Pick<BuilderFontCustom, 'family' | 'id' | 'stretch' | 'style' | 'weight'> & {
    extension: string;
};

export function generateFontCssSrc(customFont: GenerateFontCssDto, fontUrl?: string): string {
    return `url("${fontUrl || getFontLocalUrl(customFont.id)}")${getFontFormat(customFont.extension)}`;
}

export function generateFontCss(customFont: GenerateFontCssDto, fontUrl?: string): string {
    // language=CSS
    return stripIndent`
        @font-face {
            font-family: '${customFont.family}';
            font-stretch: ${customFont.stretch};
            font-style: ${customFont.style};
            font-weight: ${customFont.weight};
            src: ${generateFontCssSrc(customFont, fontUrl)};
        }
    `;
}

export const BuilderFontApi = {
    getFontsForClient: new ApiRoute<BuilderFontConfig, { clientId: ClientId }, void, void>(HttpMethod.Get, path =>
        path.literal('clients').param('clientId').literal('fonts'),
    ),
};
